import { useMemo, useState } from "react";
import type { WordDTO } from "@shared/types";
import { ChallengeRouter } from "./challenges/ChallengeRouter";

interface Props {
  missed: WordDTO[];
  allWords: WordDTO[];
  onDone: (corrected: { wordId: string; correct: boolean }[]) => void;
}

/**
 * After a run, show each missed word again and force the student to answer
 * correctly to advance. Each word uses an easy English picture variant so the
 * review feels like reinforcement, not punishment. The result
 * of each re-attempt is reported back so the SRS state can be updated.
 */
export function MissedWordReview({ missed, allWords, onDone }: Props) {
  const [i, setI] = useState(0);
  const [results, setResults] = useState<{ wordId: string; correct: boolean }[]>([]);
  const word = missed[i];

  const distractors = useMemo(() => {
    if (!word) return [];
    return allWords.filter((w) => w.id !== word.id).sort(() => Math.random() - 0.5).slice(0, 3);
  }, [word?.id, allWords]);

  if (!word) return null;

  const handle = (correct: boolean) => {
    const next = [...results, { wordId: word.id, correct }];
    setResults(next);
    if (!correct) {
      // Force re-attempt — stay on the same word.
      return;
    }
    if (i + 1 >= missed.length) onDone(next);
    else setI(i + 1);
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 12,
          left: 0,
          right: 0,
          textAlign: "center",
          color: "#fff",
          zIndex: 11,
          pointerEvents: "none",
          textShadow: "0 1px 4px #000a",
        }}
      >
        <strong>Review</strong> · word {i + 1} of {missed.length}
      </div>
      <ChallengeRouter
        word={word}
        distractors={distractors}
        onAnswer={handle}
        forceKind="word-to-image"
      />
    </>
  );
}
